import { useLayoutEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { matchRoutes, useLocation } from "react-router-dom";
import type { AppRoutes } from "config/routes";

const APP_TITLE = process.env.REACT_APP_TITLE ?? "Output Hub";

/**
 * Keeps `document.title` in sync with the active route so every screen has a
 * unique, descriptive page title (WCAG 2.4.2 Page Titled).
 *
 * The title is resolved from the most specific matching route; paths that match
 * no route (a Not Found page) fall back to the "Page not found" title. The app
 * name is always appended.
 */
function useDocumentTitle(routes: AppRoutes[]): void {
  const { t } = useTranslation();
  const { pathname } = useLocation();

  const title = useMemo<string>(() => {
    const matches = matchRoutes(
      routes.map((route) => ({ path: route.path, handle: { titleKey: route.titleKey } })),
      pathname
    );
    const matched = matches?.[matches.length - 1];
    const titleKey = (matched?.route.handle as { titleKey?: string } | undefined)?.titleKey;

    if (!matches || !titleKey) {
      console.warn(`No route title configured for path: ${pathname}`);
      return t("pages.not_found_title");
    }

    return t(titleKey);
  }, [routes, pathname, t]);

  useLayoutEffect(() => {
    window.document.title = `${title} | ${APP_TITLE}`;
  }, [title]);
}

export default useDocumentTitle;
